import type { Campaign, Client, MediaPlanFlatten, User } from '@skyrise-eng/types';
import type { ChannelListItem, MediaPlanDetails } from '@/models';
import { DEFAULT_CURRENCY, type CurrencyCode } from '@/constants/currency';

type RefLike = string | { _id?: string; name?: string; campaignId?: string; firstName?: string; lastName?: string };

function refId(ref: RefLike | undefined): string | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'string') return ref;
  return ref._id;
}

function resolveClient(ref: RefLike | undefined, clients?: Client[]): Client | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'object' && ref.name) return ref as Client;
  const id = refId(ref);
  if (!id) return undefined;
  return clients?.find((c) => c._id === id) ?? ({ _id: id, name: '' } as Client);
}

function resolveManager(ref: RefLike | undefined, users?: User[]): User | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'object' && (ref.firstName != null || ref.lastName != null)) return ref as User;
  const id = refId(ref);
  if (!id) return undefined;
  const user = users?.find((u) => u._id === id);
  return user ?? ({ _id: id, firstName: '', lastName: '' } as User);
}

function resolveCampaign(ref: RefLike | undefined, campaigns?: Campaign[]): Campaign | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'object' && ref.name) return ref as unknown as Campaign;
  const id = refId(ref);
  if (!id) return undefined;
  return campaigns?.find((c) => c._id === id);
}

/** Plan currency from the plan document, or inferred from line items when missing (legacy plans). */
export function resolveMediaPlanCurrency(
  plan?: Pick<MediaPlanDetails, 'currency'> | null,
  lineItemCurrencies?: (string | null | undefined)[]
): CurrencyCode {
  const fromPlan = plan?.currency?.trim().toUpperCase();
  if (fromPlan && /^[A-Z]{3}$/.test(fromPlan)) return fromPlan as CurrencyCode;

  const counts = new Map<string, number>();
  for (const raw of lineItemCurrencies ?? []) {
    const code = raw?.trim().toUpperCase();
    if (!code || !/^[A-Z]{3}$/.test(code)) continue;
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  if (counts.size > 0) {
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0] as CurrencyCode;
  }

  return DEFAULT_CURRENCY;
}

export function getMediaPlanTotalBudget(plan?: Pick<MediaPlanFlatten, 'totalBudget' | 'campaign'> | null): number | undefined {
  if (!plan) return undefined;

  const root =
    plan.totalBudget != null && Number.isFinite(Number(plan.totalBudget)) ? Number(plan.totalBudget) : undefined;
  const nestedRaw = (plan.campaign as Campaign | undefined)?.totalBudget;
  const nested =
    nestedRaw != null && Number.isFinite(Number(nestedRaw)) ? Number(nestedRaw) : undefined;

  // When both exist, use the higher value as the allocation cap so line-item sums that
  // match the campaign budget don't show >100% if root totalBudget was stale.
  if (root != null && nested != null) return Math.max(root, nested);
  return root ?? nested;
}

/** Allocated budget as % of plan total (gross cap, same basis as line-item budgets). */
export function getMediaPlanAllocationPercent(
  allocatedBudget: number | undefined,
  planTotalBudget: number | undefined
): number {
  if (planTotalBudget == null || planTotalBudget <= 0) return 0;
  const allocated = allocatedBudget ?? 0;
  if (Math.abs(allocated - planTotalBudget) < 0.01) return 100;
  return (allocated * 100) / planTotalBudget;
}

/** Plan-level hard costs from API, or sum of line items when plan field is absent (legacy). */
export function getMediaPlanTotalHardCosts(
  plan?: Pick<MediaPlanDetails, 'totalHardCosts'> | null,
  lineItems?: Pick<ChannelListItem, 'hardCost'>[]
): number {
  const doc = plan as { totalHardCosts?: number; total_hard_costs?: number } | null | undefined;
  const fromPlan = doc?.totalHardCosts ?? doc?.total_hard_costs;
  if (fromPlan != null && Number.isFinite(Number(fromPlan))) {
    return Number(fromPlan);
  }
  return (lineItems ?? []).reduce((sum, line) => sum + (Number(line.hardCost) || 0), 0);
}

/** Normalize GET /media-plans/:id when refs are ids only and budget fields differ by version. */
export function normalizeMediaPlanResponse(raw: MediaPlanDetails): MediaPlanDetails {
  const doc = raw as MediaPlanDetails & {
    total_budget?: number;
    margin_percent?: number;
    impressions?: number;
    total_hard_costs?: number;
  };

  const totalBudget = getMediaPlanTotalBudget(doc) ?? doc.total_budget;
  const marginPercent =
    doc.marginPercent ?? doc.margin_percent ?? (doc as { margin?: number }).margin;
  const impressions = doc.impressions;
  const totalHardCosts = doc.totalHardCosts ?? doc.total_hard_costs;

  return {
    ...doc,
    totalBudget: totalBudget ?? doc.totalBudget,
    marginPercent: marginPercent ?? doc.marginPercent,
    impressions: impressions ?? doc.impressions,
    totalHardCosts: totalHardCosts ?? doc.totalHardCosts,
    currency: doc.currency?.trim() || undefined
  };
}

export function enrichMediaPlanDetails(
  raw: MediaPlanDetails | undefined,
  lookups: { clients?: Client[]; users?: User[]; campaigns?: Campaign[] }
): MediaPlanDetails | undefined {
  if (!raw) return undefined;

  const plan = normalizeMediaPlanResponse(raw);
  const clientId = refId(plan.client as RefLike);
  const client = resolveClient(plan.client as unknown as RefLike, lookups.clients);
  const manager = resolveManager(plan.manager as unknown as RefLike, lookups.users);
  const campaign =
    resolveCampaign(plan.campaign as unknown as RefLike, lookups.campaigns) ??
    (typeof plan.campaign === 'object' ? (plan.campaign as Campaign) : undefined);

  const totalBudget = getMediaPlanTotalBudget(plan);

  return {
    ...plan,
    totalBudget,
    client: client ?? plan.client,
    manager: manager ?? plan.manager,
    campaign: campaign
      ? { ...campaign, totalBudget: totalBudget ?? campaign.totalBudget }
      : plan.campaign
  } as MediaPlanDetails;
}

export function getMediaPlanClientName(plan?: MediaPlanDetails | null): string {
  const client = plan?.client;
  if (client && typeof client === 'object' && 'name' in client && client.name) return client.name;
  return '';
}

export function getMediaPlanManagerName(plan?: MediaPlanDetails | null): string {
  const manager = plan?.manager;
  if (!manager || typeof manager !== 'object') return '';
  return [manager.firstName, manager.lastName].filter(Boolean).join(' ').trim();
}

export function getMediaPlanDealId(plan?: MediaPlanDetails | null): string | undefined {
  const campaign = plan?.campaign;
  if (!campaign || typeof campaign !== 'object') return undefined;
  return (campaign as Campaign & { campaignId?: string }).campaignId;
}

/**
 * PO Number that will end up on the raised IO: the plan-level override if set, else the
 * PipeDrive deal snapshot's PO (mirrors the backend's own resolution in raise-io). Neither
 * `poNumber` nor `dealSnapshot` are declared on the shared `@skyrise-eng/types` MediaPlan
 * type, but the backend document includes them at runtime — same pattern as `currency` /
 * `totalHardCosts` elsewhere in this file.
 *
 * Returns `undefined` while genuinely unknown (e.g. plan not loaded yet), and `''` once
 * it is known there is no PO on either the plan or the deal — callers use that distinction
 * to decide whether to show the "PO will be blank" warning.
 */
export function resolveMediaPlanPoNumber(plan?: object | null): string | undefined {
  if (!plan) return undefined;
  const doc = plan as { poNumber?: string; dealSnapshot?: { poNumber?: string } };
  const own = doc.poNumber?.trim();
  if (own) return own;
  const fromDeal = doc.dealSnapshot?.poNumber?.trim();
  return fromDeal || '';
}
